import React, { useState, useMemo, useContext, useRef, useEffect } from 'react';

import ContextMenu from '../global/ContextMenu';

import IssuesModal from  '@/components/modals/ResolveLockerIssuesModal';
import FilterHeader from '../global/FilterHeader';
import NotFound from './NotFound';

import { LockerManagementContext } from '@/context/LockerManagmentContext';
import { LockerEventStatus, LockerContextRefreshType as Table, ModalProps } from  '@/interfaces/LockerManagement';
import { ToastMessageType } from  '@/components/elements/global/ToastNotification';
import { BuildPath } from  '@/helpers/Paths';
import useOnScreen from  '@/components/hooks/useOnScreen';
import { download, getFileTimeStamp } from  '@/helpers/Files';
import useBatchIngestNotificaitons from  '@/components/hooks/Locker/useBatchIngest';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Truncate } from  '@/helpers/Text';


const EventsManagementTable = ({setToast, orgId}:{setToast: (messageType: ToastMessageType, message: JSX.Element) => void, orgId: number}) => {
   
     /* Ref for infinite scrolling, if there are more files, load them and append to files when this comes into view */
    const scrollTo = useRef();
    const isVisible = useOnScreen(scrollTo, document.querySelector('.table-wrapper.events'));
    
    const [showModal, setShowModal] = useState<boolean>(false);
    
    const [modalContents, setModalContents] = useState<ModalProps>();
    
    const { events, nextSkip, isLoading, searchParams, searchAndFilter, refreshData, nextPage } = useContext(LockerManagementContext);
 
    useEffect(()=> {
        isVisible && (events.length === nextSkip) && nextPage(Table.EVENTS);
    },[isVisible, nextSkip])
   
    function generateContextOptions(fileName: string, id: number, status: LockerEventStatus) {
        let contextOptions = []

        if(status === LockerEventStatus.WITHERRORS) {
            contextOptions.push({
                label: 'View Issues',
                onClick: () => {
                    showIssuesModal(fileName, id)
                }
            });
        }
        
        return [
            ...contextOptions,
            {
                label: 'View History',
                onClick: () => {
                    download(
                        `../../api/locker/batches/${id}/history/download`,
                    `${fileName}_HistoryReport_${getFileTimeStamp()}.csv`
                    )
                }
            }
            
        ]
    }
    
    function TableRows(): JSX.Element  {
        return (
        <>
            {events.map((event, idx) => (
                <tr key={`table-row-${idx}`}>
                    <td>
                        <span className="lockerfile-batchname">
                            <img src={`${BuildPath()}/images/locker/fileIcons.svg`} alt="batch icon"/>
                            {event.lockerBatchName.length >= 18 &&
                            <ReactTooltip id={`tooltip-batchName-${event.lockerBatchId}`} place="top" className='thrive-tooltip no-width'  aria-hidden="true">
                                <span>{event.lockerBatchName}</span>
                            </ReactTooltip> 
                            }
                            <span data-tooltip-id={`tooltip-batchName-${event.lockerBatchId}`}>
                                {Truncate(event.lockerBatchName, 18)}
                            </span>
                        </span>
                    </td>
                    <td>{new Date(event.uploadDate).toLocaleDateString('en-gb')}</td>
                    <td>{event.intentFileCount}</td>
                    <td>{event.intentAliasCount}</td>
                    <td>{getStatus(event.lockerBatchStatus)}</td>
                    <td className="more-details-button-wrapper">
                        <ContextMenu
                            className="more-details-button"
                            contextLabel={`More Details - ${event.lockerBatchName}`}
                            options={generateContextOptions(event.lockerBatchName, event.lockerBatchId, event.lockerBatchStatus)}
                            closeOnScrollSelector={'.table-wrapper'}
                        />
                    </td>
                </tr>
            ))}
        </>
        );
    }

    function getStatus(status: LockerEventStatus): JSX.Element {
        switch(status) {
            case LockerEventStatus.COMPLETED:
                return <span className="event-status completed">Completed</span>
            case LockerEventStatus.FAILED:
                return <span className="event-status failed">Failed</span>
            case LockerEventStatus.WITHERRORS:
                return <span className="event-status errors">Received with errors</span>
            case LockerEventStatus.PROCESSING:
                return (
                <span className="event-status processing">
                    <img alt='Processing' src={`${BuildPath()}/images/userupload/Rolling.svg`}/> 
                    Processing
                </span>)
        }
    }   

    function showIssuesModal (fileName: string, id: number) {
        setShowModal(true);
        setModalContents({
            title: fileName,
            id,
            onClose: () => setShowModal(false),
            onConfirm: (messageType, message) => {
                setToast(messageType, message)
                refreshData(Table.EVENTS)
            }
        });
    }

    function Modal({showModal}:{showModal: boolean}): JSX.Element {
        return showModal
            ? (
                <IssuesModal
                className='larger-mini-modal'
                onConfirm={modalContents.onConfirm}
                showModal={showModal}
                onClose={modalContents.onClose}
                title={modalContents.title}
                id={modalContents.id}
             />
            )
            : <></>
    }
    
    return (
    <>
        <div className='anti-floating-text-div' />
        <div className={`table-wrapper event narrow-scroll-bar`}>
            <table className={`events ${events.length > 13 ? 'scroll-table': ''}`}>
                <thead>
                    <tr>
                        <FilterHeader
                            columnNames={['Batch ID', 'Upload Date', 'Files', 'Users/Groups', 'Status']}
                            setFilter={(filter) => searchAndFilter(Table.EVENTS, filter)}
                            currentFilters={searchParams[Table.EVENTS]}
                            disabledColumns={['Status']}
                        />
                        <th>Details</th>
                    </tr> 
                </thead>
                <tbody className={isLoading ? ' loading' : ''}>
                {events.length 
                    ? <TableRows />
                    : null
                }
                
                </tbody>
            </table>
            
            <div className={`next-page-wrapper`} ref={scrollTo} />

            {!events.length &&
                <div className="not-found-wrapper">
                    <NotFound message="You have no locker events" table={Table.EVENTS}/>      
                 </div>


            }
            {useMemo(() => <Modal showModal={showModal}/>, [showModal])}
               
        </div>
    </>
       
)}

export default EventsManagementTable